import React, { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import styles from '../styles/styles.module.css';
import { states } from '../data/states';

import {
  resetState,
  setCompanyCity,
  setCompanyDomain,
  setCompanyName,
  setCompanyPhone,
  setCompanyState,
  setCompanyStreet,
  setCompanyZip,
  setRepEmail,
  setRepFirstName,
  setRepLastName,
  setWeekStart,
} from '../../../Redux/slices/companySetupSlice';

import { createCompany } from '../../../api/company/post';
import { setupCompanyMessagingBrand } from '../../../api/messaging/post';

const ENTITY_TYPES = [
  {
    value: 'PRIVATE_PROFIT',
    label: 'Private For-Profit',
  },
  {
    value: 'PUBLIC_PROFIT',
    label: 'Public For-Profit',
  },
  {
    value: 'NON_PROFIT',
    label: 'Non-Profit',
  },
  {
    value: 'GOVERNMENT',
    label: 'Government',
  },
  {
    value: 'SOLE_PROPRIETOR',
    label: 'Sole Proprietor',
  },
];

const BUSINESS_VERTICALS = [
  { value: 'AGRICULTURE', label: 'Agriculture' },
  { value: 'COMMUNICATION', label: 'Communication' },
  { value: 'CONSTRUCTION', label: 'Construction' },
  { value: 'EDUCATION', label: 'Education' },
  { value: 'ENERGY', label: 'Energy' },
  { value: 'ENTERTAINMENT', label: 'Entertainment' },
  { value: 'FINANCIAL', label: 'Financial' },
  { value: 'GAMBLING', label: 'Gambling' },
  { value: 'GOVERNMENT', label: 'Government' },
  { value: 'HEALTHCARE', label: 'Healthcare' },
  { value: 'HOSPITALITY', label: 'Hospitality' },
  { value: 'HUMAN_RESOURCES', label: 'Human Resources' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'LEGAL', label: 'Legal' },
  { value: 'MANUFACTURING', label: 'Manufacturing' },
  { value: 'NGO', label: 'NGO' },
  { value: 'POLITICAL', label: 'Political' },
  { value: 'POSTAL', label: 'Postal' },
  { value: 'PROFESSIONAL', label: 'Professional Services' },
  { value: 'REAL_ESTATE', label: 'Real Estate' },
  { value: 'RETAIL', label: 'Retail' },
  { value: 'TECHNOLOGY', label: 'Technology' },
  { value: 'TRANSPORTATION', label: 'Transportation' },
];

const CompanySetup = () => {
  const dispatch = useDispatch();

  const {
    weekStart,
    companyDomain,
    companyName,
    companyRepresentativeFirstName,
    companyRepresentativeLastName,
    companyRepresentativeEmail,
    companyStreet,
    companyCity,
    companyState,
    companyZip,
    companyPhone,
  } = useSelector((state: any) => state.companySetup);

  const [loading, setLoading] = useState(false);

  /*
   * Once the EdLogger Company has been created,
   * preserve its ID.
   *
   * If Telnyx fails, clicking Submit again retries
   * messaging setup without creating a duplicate company.
   */
  const [createdCompanyId, setCreatedCompanyId] = useState<string | null>(null);

  /*
   * Telnyx / 10DLC-specific fields.
   */
  const [legalCompanyName, setLegalCompanyName] = useState('');

  const [ein, setEin] = useState('');

  const [entityType, setEntityType] = useState('PRIVATE_PROFIT');

  const [vertical, setVertical] = useState('PROFESSIONAL');

  /*
   * false:
   * We are registering our own organization.
   *
   * true:
   * EdLogger is registering a separate customer
   * organization on its behalf.
   *
   * For the MSB / ExecuDev test, leave this false.
   */
  const [isReseller, setIsReseller] = useState(false);

  /**
   * Convert any phone input into the 10-digit
   * U.S. subscriber number.
   *
   * Examples:
   *
   * 8052852807
   * 18052852807
   * +1 (805) 285-2807
   *
   * all become:
   *
   * 8052852807
   */
  const getUSPhoneDigits = (value: string): string => {
    let digits = value.replace(/\D/g, '');

    if (digits.length > 10 && digits.startsWith('1')) {
      digits = digits.substring(1);
    }

    return digits.slice(0, 10);
  };

  /**
   * Format phone for display in the form.
   *
   * Example:
   *
   * +1 (805) 285-2807
   */
  const formatUSPhoneForDisplay = (value: string): string => {
    const digits = getUSPhoneDigits(value);

    if (digits.length === 0) {
      return '+1 ';
    }

    if (digits.length <= 3) {
      return `+1 (${digits}`;
    }

    if (digits.length <= 6) {
      return `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }

    return `+1 (${digits.slice(0, 3)}) ${digits.slice(
      3,
      6
    )}-${digits.slice(6, 10)}`;
  };

  /**
   * Convert phone to Telnyx E.164 format.
   *
   * Example:
   *
   * +1 (805) 285-2807
   *
   * becomes:
   *
   * +18052852807
   */
  const formatPhoneForTelnyx = (value: string): string => {
    const digits = getUSPhoneDigits(value);

    return `+1${digits}`;
  };

  /**
   * Live-format EIN:
   *
   * 123456789
   *
   * becomes:
   *
   * 12-3456789
   */
  const formatEin = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 9);

    if (digits.length <= 2) {
      return digits;
    }

    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  };

  /**
   * Telnyx only needs the 5-digit base ZIP.
   *
   * 93065-3388 -> 93065
   */
  const formatPostalCodeForTelnyx = (zip: string): string => {
    return zip.trim().split('-')[0];
  };

  /**
   * Company domain is stored as:
   *
   * example.com
   *
   * Telnyx receives:
   *
   * https://example.com
   */
  const buildWebsiteUrl = (domain: string): string => {
    const trimmed = domain.trim();

    if (trimmed.startsWith('https://') || trimmed.startsWith('http://')) {
      return trimmed;
    }

    return `https://${trimmed}`;
  };

  const validateFields = () => {
    const errors: string[] = [];

    if (!companyName.trim()) {
      errors.push('Company name is required.');
    }

    if (!legalCompanyName.trim()) {
      errors.push('Legal business name is required.');
    }

    const normalizedEin = ein.replace(/\D/g, '');

    if (!/^\d{9}$/.test(normalizedEin)) {
      errors.push('EIN must contain exactly 9 digits.');
    }

    if (!entityType) {
      errors.push('Business entity type is required.');
    }

    if (!vertical) {
      errors.push('Business vertical is required.');
    }

    if (
      !companyDomain.trim() ||
      !/^(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/.test(companyDomain.trim())
    ) {
      errors.push('Company domain must be a valid domain (e.g., example.com).');
    }

    const phoneDigits = getUSPhoneDigits(companyPhone);

    if (phoneDigits.length !== 10) {
      errors.push('Company phone must be a valid 10-digit U.S. phone number.');
    }

    if (!companyStreet.trim()) {
      errors.push('Company street is required.');
    }

    if (!companyCity.trim()) {
      errors.push('Company city is required.');
    }

    if (!companyState.trim()) {
      errors.push('Company state is required.');
    }

    if (!companyZip.trim() || !/^\d{5}(-\d{4})?$/.test(companyZip.trim())) {
      errors.push('Company ZIP must be a valid ZIP code.');
    }

    if (!companyRepresentativeFirstName.trim()) {
      errors.push('Company admin first name is required.');
    }

    if (!companyRepresentativeLastName.trim()) {
      errors.push('Company admin last name is required.');
    }

    if (!companyRepresentativeEmail.trim()) {
      errors.push('Company admin email is required.');
    }

    if (
      companyRepresentativeEmail.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyRepresentativeEmail.trim())
    ) {
      errors.push('Company admin email must be valid.');
    }

    return errors;
  };

  const resetMessagingFields = () => {
    setLegalCompanyName('');
    setEin('');
    setEntityType('PRIVATE_PROFIT');
    setVertical('PROFESSIONAL');
    setIsReseller(false);
    setCreatedCompanyId(null);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatUSPhoneForDisplay(e.target.value);

    dispatch(setCompanyPhone(formatted));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const errors = validateFields();

    if (errors.length > 0) {
      alert(`Validation Errors:\n${errors.join('\n')}`);

      return;
    }

    setLoading(true);

    try {
      /*
       * --------------------------------------------------
       * STEP 1
       * CREATE EDLOGGER COMPANY
       * --------------------------------------------------
       */

      let companyId = createdCompanyId;

      if (!companyId) {
        const companyPayload = {
          weekStart,

          companyName: companyName.trim(),

          companyDomain: companyDomain.trim(),

          /*
           * Store consistently with +1 formatting.
           */
          companyPhone: formatUSPhoneForDisplay(companyPhone),

          companyStreet: companyStreet.trim(),

          companyCity: companyCity.trim(),

          companyState: companyState.trim(),

          companyZip: companyZip.trim(),

          companyRepresentativeFirstName: companyRepresentativeFirstName.trim(),

          companyRepresentativeLastName: companyRepresentativeLastName.trim(),

          companyRepresentativeEmail: companyRepresentativeEmail
            .trim()
            .toLowerCase(),

          userType: 'COMPANY_ADMIN',
        };

        const companyResponse = await createCompany(companyPayload);

        if (!companyResponse?.success) {
          alert(companyResponse?.msg || 'Failed to create company.');

          return;
        }

        const createdCompany: any = companyResponse.data;

        companyId = createdCompany?._id;

        if (!companyId) {
          throw new Error(
            'Company was created but no company ID was returned.'
          );
        }

        /*
         * Preserve the ID immediately.
         *
         * If Telnyx fails below, we retry
         * messaging against this same company.
         */
        setCreatedCompanyId(companyId);
      }

      /*
       * --------------------------------------------------
       * STEP 2
       * CREATE TELNYX 10DLC BRAND
       * --------------------------------------------------
       */

      const messagingPayload = {
        /*
         * Public-facing Brand.
         *
         * Example:
         * MSB Protection
         */
        displayName: companyName.trim(),

        /*
         * Legal company name associated with EIN.
         *
         * Example:
         * ExecuDev, Inc.
         */
        companyName: legalCompanyName.trim(),

        email: companyRepresentativeEmail.trim().toLowerCase(),

        entityType,

        vertical,

        ein: ein.replace(/\D/g, ''),

        firstName: companyRepresentativeFirstName.trim(),

        lastName: companyRepresentativeLastName.trim(),

        /*
         * Always:
         *
         * +1XXXXXXXXXX
         */
        phone: formatPhoneForTelnyx(companyPhone),

        street: companyStreet.trim(),

        city: companyCity.trim(),

        state: companyState.trim(),

        postalCode: formatPostalCodeForTelnyx(companyZip),

        website: buildWebsiteUrl(companyDomain),

        /*
         * MSB / ExecuDev:
         * false
         *
         * Customer company:
         * true
         */
        isReseller,
      };

      console.log('📨 10DLC Brand registration:', {
        companyId,
        displayName: messagingPayload.displayName,
        legalCompanyName: messagingPayload.companyName,
        phone: messagingPayload.phone,
        isReseller: messagingPayload.isReseller,
      });

      const messagingResponse = await setupCompanyMessagingBrand(
        companyId,
        messagingPayload
      );

      if (!messagingResponse?.success) {
        alert(
          `Company was created successfully, but messaging setup failed.\n\n${
            messagingResponse?.msg || 'Unknown messaging setup error'
          }\n\nCorrect the messaging information and click Submit again to retry.`
        );

        return;
      }

      /*
       * --------------------------------------------------
       * COMPLETE
       * --------------------------------------------------
       */

      alert('Company and messaging setup completed successfully.');

      dispatch(resetState());

      resetMessagingFields();
    } catch (error: any) {
      console.error('Company setup error:', error);

      const backendMessage = error?.response?.data?.msg;

      if (backendMessage) {
        alert(backendMessage);
      } else if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(String(error));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.subPageWrapper}>
      <div className={styles.formWrapper}>
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* COMPANY / 10DLC INFORMATION */}
          <div className={styles.leftContainer}>
            <div className={styles.inputContainer}>
              <p className={styles.label}>Company Name</p>

              <input
                required
                value={companyName}
                onChange={(e) => dispatch(setCompanyName(e.target.value))}
                className={styles.inputElement}
                placeholder="MSB Protection"
              />
            </div>

            <div className={styles.inputContainer}>
              <p className={styles.label}>Legal Business Name</p>

              <input
                required
                value={legalCompanyName}
                onChange={(e) => setLegalCompanyName(e.target.value)}
                className={styles.inputElement}
                placeholder="ExecuDev, Inc."
              />
            </div>

            <div className={styles.inputContainer}>
              <p className={styles.label}>EIN</p>

              <input
                required
                type="text"
                inputMode="numeric"
                maxLength={10}
                value={ein}
                onChange={(e) => setEin(formatEin(e.target.value))}
                className={styles.inputElement}
                placeholder="12-3456789"
              />
            </div>

            <div className={styles.inputContainer}>
              <p className={styles.label}>Entity Type</p>

              <select
                value={entityType}
                onChange={(e) => setEntityType(e.target.value)}
                className={styles.inputElement}
              >
                {ENTITY_TYPES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.inputContainer}>
              <p className={styles.label}>Business Vertical</p>

              <select
                value={vertical}
                onChange={(e) => setVertical(e.target.value)}
                className={styles.inputElement}
              >
                {BUSINESS_VERTICALS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.inputContainer}>
              <p className={styles.label}>10DLC Registration</p>

              <select
                value={isReseller ? 'CUSTOMER' : 'OWN'}
                onChange={(e) => setIsReseller(e.target.value === 'CUSTOMER')}
                className={styles.inputElement}
              >
                <option value="OWN">This is my organization</option>

                <option value="CUSTOMER">
                  Registering on behalf of another organization
                </option>
              </select>
            </div>

            <div className={styles.inputContainer}>
              <p className={styles.label}>Week Start</p>

              <select
                value={weekStart}
                onChange={(e) => dispatch(setWeekStart(e.target.value))}
                className={styles.inputElement}
              >
                <option value="Mon">Monday</option>

                <option value="Sun">Sunday</option>
              </select>
            </div>
          </div>

          {/* COMPANY CONTACT / ADDRESS */}
          <div className={styles.rightContainer}>
            <div className={styles.inputContainer}>
              <p className={styles.label}>Company Domain</p>

              <input
                required
                value={companyDomain}
                onChange={(e) => dispatch(setCompanyDomain(e.target.value))}
                className={styles.inputElement}
                placeholder="example.com"
              />
            </div>

            <div className={styles.inputContainer}>
              <p className={styles.label}>Company Phone</p>

              <input
                required
                type="tel"
                inputMode="tel"
                value={companyPhone || '+1 '}
                onFocus={() => {
                  if (!companyPhone) {
                    dispatch(setCompanyPhone('+1 '));
                  }
                }}
                onChange={handlePhoneChange}
                className={styles.inputElement}
                placeholder="+1 (805) 555-1234"
              />
            </div>

            <div className={styles.inputContainer}>
              <p className={styles.label}>Company Street</p>

              <input
                required
                value={companyStreet}
                onChange={(e) => dispatch(setCompanyStreet(e.target.value))}
                className={styles.inputElement}
              />
            </div>

            <div className={styles.inputContainer}>
              <p className={styles.label}>Company City</p>

              <input
                required
                value={companyCity}
                onChange={(e) => dispatch(setCompanyCity(e.target.value))}
                className={styles.inputElement}
              />
            </div>

            <div className={styles.stateAndZip}>
              <div className={styles.inputContainerSuperSmall}>
                <p className={styles.label}>State</p>

                <select
                  value={companyState}
                  onChange={(e) => dispatch(setCompanyState(e.target.value))}
                  className={styles.stateDropdown}
                >
                  {states.map((state: string) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.inputContainerSmall}>
                <p className={styles.label}>Company Zip</p>

                <input
                  required
                  value={companyZip}
                  onChange={(e) => dispatch(setCompanyZip(e.target.value))}
                  className={styles.inputElement}
                  placeholder="93065"
                />
              </div>
            </div>
          </div>

          <button
            disabled={loading}
            type="submit"
            className={styles.companySetupSubmit}
          >
            {loading
              ? 'Please wait...'
              : createdCompanyId
                ? 'Retry Messaging Setup'
                : 'Create Company'}
          </button>
        </form>

        {/* COMPANY ADMIN */}
        <div className={styles.addEmails}>
          <p className={styles.representativesHeadline}>Company Admin</p>

          <div className={styles.representativesWrapper}>
            <div className={styles.inputContainer}>
              <p className={styles.label}>First Name</p>

              <input
                required
                value={companyRepresentativeFirstName}
                onChange={(e) => dispatch(setRepFirstName(e.target.value))}
                className={styles.inputElement}
              />
            </div>

            <div className={styles.inputContainer}>
              <p className={styles.label}>Last Name</p>

              <input
                required
                value={companyRepresentativeLastName}
                onChange={(e) => dispatch(setRepLastName(e.target.value))}
                className={styles.inputElement}
              />
            </div>

            <div className={styles.inputContainer}>
              <p className={styles.label}>Email</p>

              <input
                required
                type="email"
                value={companyRepresentativeEmail}
                onChange={(e) => dispatch(setRepEmail(e.target.value))}
                className={styles.inputElement}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySetup;
